// ============================================
// APP: NVA (React App)
// FILE: src/lib/featuredEvent.ts
// PURPOSE: L'evento "Da non perdere" della pagina Eventi, scelto con una
//   regola semplice e pura (niente React, niente rete: si prova da uno
//   script), più i piccoli calcoli di tempo che servono anche alle card:
//   "in corso", "tra N minuti", "ha una foto vera".
//
//   Giorni: "oggi" è il giorno di Roma (romeDayKey); il giorno di un evento
//   si legge dai campi locali della data, come in Esplora (le date del sito
//   sono mezzanotti del telefono). Un'ora a 00:00 vuol dire "ora non nota".
// ============================================

import { dayKey } from './eventsParser';
import { dayKeyDiff, romeDayKey, shiftDayKey } from './romeDay';

/** Il minimo di un evento che serve qui: così la funzione non dipende da EventsContext. */
export interface TimedEvent {
  title: string;
  startDate: Date;
  endDate?: Date;
  city: string;
  imageUrl?: string;
  /** Km da Mola (0 per Mola), quando li ha dati /api/events. */
  distanceKm?: number;
}

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
/** Una serata con l'ora (concerto, spettacolo) resta "in corso" per 3 ore dall'inizio. */
const EVENING_ONGOING_MS = 3 * HOUR_MS;
/** Candidati: in corso o che iniziano entro 48 ore; se nessuno, i prossimi 7 giorni. */
const CANDIDATE_HOURS = 48;
const FALLBACK_DAYS = 7;
/** "+2 se è in corso o inizia entro 6 ore". */
const SOON_HOURS = 6;
/** "+1 se è entro 10 km" (Mola ha già il suo +2). */
const NEAR_KM = 10;
const MOLA = 'Mola di Bari';

/** Le parole dei "grandi eventi" (anche al plurale), confrontate senza maiuscole né accenti. */
const BIG_EVENT_WORDS = [
  'festa', 'feste', 'sagra', 'sagre', 'festival', 'fiera', 'fiere', 'notte bianca',
  'patronale', 'patronali', 'concerto', 'concerti', 'carnevale', 'palio',
  'processione', 'processioni', 'mercatino', 'mercatini', 'degustazione', 'degustazioni',
];

/** Minuscolo e senza accenti: "Festività" → "festivita". */
export function normalizeText(text: string): string {
  return String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

const BIG_EVENT_RE = new RegExp(`\\b(?:${BIG_EVENT_WORDS.map((w) => w.replace(/ /g, '\\s+')).join('|')})\\b`);

export function isBigEvent(title: string): boolean {
  return BIG_EVENT_RE.test(normalizeText(title));
}

/** L'ora è nota (le date del sito senza ora sono a mezzanotte). */
export function hasEventTime(date: Date): boolean {
  return date.getHours() !== 0 || date.getMinutes() !== 0;
}

/**
 * Una foto vera: il sito, quando non ne ha, mette l'icona della categoria
 * (/i/musica-e-concerti.png), che allargata a tutta la card non va.
 */
export function hasEventPhoto(event: Pick<TimedEvent, 'imageUrl'>): boolean {
  return !!event.imageUrl && !/\/i\/[^/]+\.(png|gif|svg)$/i.test(event.imageUrl);
}

/** Primo e ultimo giorno ("AAAA-MM-GG"). */
export function eventDays(event: Pick<TimedEvent, 'startDate' | 'endDate'>): { start: string; end: string } {
  const start = dayKey(event.startDate);
  const end = event.endDate ? dayKey(event.endDate) : start;
  return { start, end: end < start ? start : end };
}

/** L'evento si svolge (anche) in un giorno fra `from` e `to` compresi. */
export function happensBetween(event: Pick<TimedEvent, 'startDate' | 'endDate'>, from: string, to: string): boolean {
  const { start, end } = eventDays(event);
  return start <= to && end >= from;
}

/**
 * In corso adesso:
 * - cominciato nei giorni scorsi e non ancora finito (mostre, feste di più giorni);
 * - di più giorni, comincia oggi, senza ora o con l'ora già passata;
 * - di un giorno con l'ora, cominciato da meno di 3 ore.
 * Un evento di un giorno senza ora non è "in corso": è "oggi".
 */
export function isEventOngoing(event: Pick<TimedEvent, 'startDate' | 'endDate'>, now: Date = new Date(), today: string = romeDayKey(now)): boolean {
  const { start, end } = eventDays(event);
  if (start > today || end < today) return false;
  if (start < today) return true;
  const multiDay = end > start;
  if (!hasEventTime(event.startDate)) return multiDay;
  const since = now.getTime() - event.startDate.getTime();
  if (since < 0) return false;
  return multiDay || since < EVENING_ONGOING_MS;
}

/** Minuti all'inizio, solo se l'ora è nota (null senza ora). Negativi se è già cominciato. */
export function minutesUntilStart(event: Pick<TimedEvent, 'startDate'>, now: Date = new Date()): number | null {
  if (!hasEventTime(event.startDate)) return null;
  return Math.round((event.startDate.getTime() - now.getTime()) / MINUTE_MS);
}

/** Giorni da oggi al giorno dell'evento (0 se è oggi o è in corso). */
function daysFromToday(event: TimedEvent, today: string): number {
  const { start } = eventDays(event);
  const diff = dayKeyDiff(start > today ? start : today, today);
  return diff === null ? 0 : Math.max(0, diff);
}

/** Inizia entro `hours` ore: con l'ora, dal suo orario; senza, dalla mezzanotte del suo giorno (oggi compreso). */
function startsWithin(event: TimedEvent, hours: number, now: Date, today: string): boolean {
  const minutes = minutesUntilStart(event, now);
  if (minutes !== null) return minutes >= 0 && minutes <= hours * 60;
  const { start } = eventDays(event);
  if (start < today) return false;
  if (start === today) return true;
  return event.startDate.getTime() - now.getTime() <= hours * HOUR_MS;
}

/** Ancora da vivere: non finito e, se di un giorno con l'ora, non cominciato da più di 3 ore. */
function stillAhead(event: TimedEvent, now: Date, today: string): boolean {
  const { start, end } = eventDays(event);
  if (end < today) return false;
  if (isEventOngoing(event, now, today)) return true;
  const minutes = minutesUntilStart(event, now);
  if (minutes !== null && start === end && start === today) return minutes >= 0;
  return true;
}

/**
 * Il punteggio (più alto = più "da non perdere"):
 * +3 foto vera; +3 titolo da grande evento; +2 più giorni (fino a 7);
 * +2 a Mola, altrimenti +1 entro 10 km; +2 in corso o entro 6 ore;
 * -1 per ogni giorno da oggi.
 */
/** Oltre questa durata un evento "di piu' giorni" non prende il bonus. */
const MULTI_DAY_MAX_DAYS = 7;

export function featuredScore(event: TimedEvent, now: Date = new Date(), today: string = romeDayKey(now)): number {
  const { start, end } = eventDays(event);
  let score = 0;
  if (hasEventPhoto(event)) score += 3;
  if (isBigEvent(event.title)) score += 3;
  // "Piu' giorni" premia sagre e feste di qualche giorno, non le mostre di
  // settimane: quelle, sempre "in corso", vincerebbero su tutto.
  if (end > start && (dayKeyDiff(end, start) ?? Infinity) <= MULTI_DAY_MAX_DAYS) score += 2;
  if (event.city === MOLA) score += 2;
  else if (typeof event.distanceKm === 'number' && event.distanceKm <= NEAR_KM) score += 1;
  if (isEventOngoing(event, now, today) || startsWithin(event, SOON_HOURS, now, today)) score += 2;
  score -= daysFromToday(event, today);
  return score;
}

/** Quanto è lontano nel tempo (ms): 0 se è in corso o è di oggi senza ora. */
function timeDistance(event: TimedEvent, now: Date, today: string): number {
  if (isEventOngoing(event, now, today)) return 0;
  const { start } = eventDays(event);
  if (!hasEventTime(event.startDate) && start === today) return 0;
  return Math.max(0, event.startDate.getTime() - now.getTime());
}

/**
 * L'evento in evidenza: fra quelli in corso o che iniziano entro 48 ore
 * (se non ce ne sono, i prossimi 7 giorni) vince il punteggio più alto;
 * a parità, il più vicino nel tempo. Null se non c'è niente.
 */
export function pickFeaturedEvent<T extends TimedEvent>(events: T[], now: Date = new Date(), today: string = romeDayKey(now)): T | null {
  const alive = events.filter((e) => stillAhead(e, now, today));
  let candidates = alive.filter((e) => isEventOngoing(e, now, today) || startsWithin(e, CANDIDATE_HOURS, now, today));
  if (candidates.length === 0) {
    const last = shiftDayKey(today, FALLBACK_DAYS - 1);
    candidates = alive.filter((e) => happensBetween(e, today, last));
  }
  let best: T | null = null;
  let bestScore = -Infinity;
  let bestDistance = Infinity;
  for (const event of candidates) {
    const score = featuredScore(event, now, today);
    const distance = timeDistance(event, now, today);
    if (score > bestScore || (score === bestScore && distance < bestDistance)) {
      best = event;
      bestScore = score;
      bestDistance = distance;
    }
  }
  return best;
}
